/**
 * 表单字段结果值的结构描述辅助方法
 */

export function buildValueDef({type, key, name, option, fields}) {
  const def = {type, key, name: name || key};
  if (option !== undefined) {
    def.option = option;
  }
  if (Array.isArray(fields) && fields.length > 0) {
    def.fields = fields;
  }
  return def;
}

/** 表单内选择类控件（含表格列） */
export const OPTION_SELECT_CONTROLS = new Set([
  'Select', 'UgoSelect', 'ZzlSelect',
  'Radio', 'UgoRadio', 'ZzlRadio',
  'Check', 'UgoCheck', 'ZzlCheck',
  'MultiSelect', 'UgoMultiSelect', 'ZzlMultiSelect',
  'InputSelect', 'UgoInputSelect', 'ZzlInputSelect',
  'BaseSelect',
]);

export const USER_SELECT_CONTROLS = new Set([
  'UserSelect', 'UgoUserSelect', 'ZzlUserSelect',
  'BaseUserSelectGrouped',
]);

const VALUE_AS_DESCRIPTIONS = {
  name: '值为选项名称，与 options[].name 一致（不要用 key、下标或 id）',
  key: '值为选项 key，与 options[].key 一致（非 simple 模式）',
  index: '值为选项在 options 数组中的下标 index（从 0 开始，BaseSelect valueType=index）',
  id: '值为选项 id，与 options[].id 一致（远程选项、用户选择等）',
};

export function isOptionSelectControl(control) {
  return OPTION_SELECT_CONTROLS.has(control);
}

export function isUserSelectControl(control) {
  return USER_SELECT_CONTROLS.has(control);
}

/**
 * 与 Select/Radio/Check 等控件 options 计算逻辑一致
 * simple / valueType=name：key 与 name 均为选项名称
 */
export function normalizeSelectOptions(config = {}) {
  let options = config.options;
  if (!options) {
    return [];
  }
  if (!Array.isArray(options)) {
    return [];
  }
  if (options.length && typeof options[0] !== 'object') {
    options = options.map((name, i) => ({
      key: i,
      name: String(name),
    }));
  }
  if (config.simple || config.valueType === 'name') {
    options = options.map((v) => {
      const name = v.name != null ? String(v.name) : String(v.key);
      return {key: name, name, ...(v.id != null ? {id: v.id} : {})};
    });
  }
  return options;
}

/** 规范化用户选项（UserSelect） */
export function normalizeUserSelectOptions(users = []) {
  return (users || []).map((u) => ({
    id: u.id,
    key: u.id,
    name: u.name,
  }));
}

/**
 * 解析选择类控件提交值的语义：name | key | index | id
 * @param {object} config 字段 config 或列 props/config 合并对象
 * @param {string} [control] 控件类型
 */
export function resolveSelectValueAs(config = {}, control) {
  if (isUserSelectControl(control)) {
    return 'id';
  }
  if (config.simple || config.valueType === 'name') {
    return 'name';
  }
  if (config.valueType === 'index') {
    return 'index';
  }
  if (config.remote || config.valueType === 'object' || config.valueType === 'id') {
    return 'id';
  }
  return 'key';
}

/** 选择类字段：说明 valueAs 及取值规则 */
export function selectOptionValueMeta(config = {}, control) {
  const valueAs = resolveSelectValueAs(config, control);
  const meta = {
    valueAs,
    description: VALUE_AS_DESCRIPTIONS[valueAs],
  };
  if (config.simple) {
    meta.simple = true;
  }
  if (config.valueType) {
    meta.valueType = config.valueType;
  }
  if (config['value-type']) {
    meta.keyType = config['value-type'];
    meta.description += `；选项 key 按 ${config['value-type']} 类型提交`;
  }
  if (config.url) {
    meta.url = config.url;
    if (!config.options || !config.options.length) {
      meta.description += '；静态 options 为空时选项来自 url';
    }
  }
  if (config.remote) {
    meta.remote = config.remote;
    meta.description += '；选项来自 remote，值为 id';
  }
  return meta;
}

/** UserSelect：值为用户 id */
export function userSelectValueMeta(config = {}) {
  const multiple = !!config.multiple;
  return {
    valueAs: 'id',
    multiple,
    description: multiple ?
      '值为用户 id 数组，元素与 options[].id 一致' :
      '值为用户 id（数字），与 options[].id 一致',
  };
}

/**
 * 组装选择类字段 option（含 options、valueAs、description）
 */
export function buildSelectFieldOption(config = {}, control) {
  const multiple = !!config.multiple;
  const option = {
    control,
    multiple,
    ...selectOptionValueMeta(config, control),
  };
  const opts = normalizeSelectOptions(config);
  if (opts.length) {
    option.options = opts;
  }
  return option;
}

/** UserSelect 字段 option */
export function buildUserSelectFieldOption(config = {}, users = []) {
  const opts = normalizeUserSelectOptions(users);
  return {
    control: config.control || 'UgoUserSelect',
    ...userSelectValueMeta(config),
    options: opts,
    ...(opts.length ? {} : {optionsNote: '用户列表来自系统会话，运行时由接口加载'}),
  };
}

export function stringValueDef(field) {
  return buildValueDef({
    type: 'string',
    key: field.key,
    name: field.name,
    option: field.config || {},
  });
}

export function numberValueDef(field) {
  return buildValueDef({
    type: 'number',
    key: field.key,
    name: field.name,
    option: field.config || {},
  });
}

export function selectValueDef(field, control = 'UgoSelect') {
  const cfg = field.config || {};
  const multiple = !!cfg.multiple;
  return buildValueDef({
    type: multiple ? 'array' : 'string',
    key: field.key,
    name: field.name,
    option: buildSelectFieldOption(cfg, control),
  });
}

export function arrayValueDef(field, option = {}) {
  return buildValueDef({
    type: 'array',
    key: field.key,
    name: field.name,
    option,
  });
}

export function fileValueDef(field) {
  return buildValueDef({
    type: 'array',
    key: field.key,
    name: field.name,
    option: {
      description: '文件列表，元素为文件对象或文件 id',
      ...(field.config || {}),
    },
  });
}

export function objectValueDef(field, fields, option = {}) {
  return buildValueDef({
    type: 'object',
    key: field.key,
    name: field.name,
    option,
    fields,
  });
}

/** 容器控件不产生独立字段值 */
export function containerValueDef() {
  return null;
}

/** 仅展示、无提交值的控件 */
export function noValueDef() {
  return null;
}

const CONTAINER_CONTROLS = new Set([
  'UgoRow', 'UgoSection', 'UgoTabs', 'UgoCol',
  'ZzlRow', 'ZzlSection', 'ZzlTabs', 'ZzlCol',
]);

const SKIP_VALUE_CONTROLS = new Set(['UgoStatic', 'UgoOption', 'UgoRef']);

const SCHEMA_CONTROL_TYPE = {
  UgoInput: 'string',
  UgoTextarea: 'string',
  UgoNumber: 'number',
  UgoDate: 'string',
  UgoDatetime: 'string',
  UgoMonth: 'string',
  UgoSelect: 'string',
  UgoRadio: 'string',
  UgoCheck: 'array',
  UgoMultiSelect: 'array',
  UgoMarkdown: 'string',
  UgoSignature: 'string',
  UgoFile: 'array',
  UgoFileUpload: 'array',
  UgoImages: 'string',
  UgoUserSelect: 'number',
  UgoInputSelect: 'string',
  UgoMark: 'number',
  UgoTable: 'array',
  UgoZzlTable: 'array',
  UgoInnerForm: 'object',
  UgoMultiForm: 'array',
};

/**
 * 无组件实例时按字段配置生成 valueDef（保证 key 来自 field.key）
 */
export function schemaValueDefFromField(field, collectChildren) {
  if (!field || !field.key) {
    return null;
  }
  if (SKIP_VALUE_CONTROLS.has(field.control)) {
    return null;
  }
  if (CONTAINER_CONTROLS.has(field.control)) {
    return typeof collectChildren === 'function' ?
      collectChildren(field.fields || []) :
      [];
  }
  const cfg = field.config || {};
  if (field.control === 'UgoInnerForm') {
    const childFields = typeof collectChildren === 'function' ?
      collectChildren(field.fields || []) :
      [];
    return buildValueDef({
      type: 'object',
      key: field.key,
      name: field.name,
      option: {
        model: cfg.model,
        description: `内嵌表单，JSON 键名必须为 "${field.key}"`,
      },
      fields: childFields,
    });
  }
  if (field.control === 'UgoMultiForm' || field.control === 'ZzlMultiForm') {
    const childFieldsRaw = typeof collectChildren === 'function' ?
      collectChildren(field.fields || []) :
      [];
    const childFields = enrichMultiFormItemFields(childFieldsRaw, cfg);
    return buildValueDef({
      type: 'array',
      key: field.key,
      name: field.name,
      option: buildMultiFormFieldOption(cfg, {fieldKey: field.key, itemFields: childFields}),
      fields: childFields,
    });
  }
  if (field.control === 'UgoUserSelect' || field.control === 'ZzlUserSelect') {
    const multiple = !!cfg.multiple;
    return buildValueDef({
      type: multiple ? 'array' : 'number',
      key: field.key,
      name: field.name,
      option: buildUserSelectFieldOption({...cfg, control: field.control}),
    });
  }
  if (field.control === 'UgoSelect' || field.control === 'UgoRadio' || field.control === 'UgoInputSelect' ||
      field.control === 'ZzlSelect' || field.control === 'ZzlRadio' || field.control === 'ZzlInputSelect') {
    const multiple = !!cfg.multiple;
    return buildValueDef({
      type: multiple ? 'array' : 'string',
      key: field.key,
      name: field.name,
      option: buildSelectFieldOption(cfg, field.control),
    });
  }
  if (field.control === 'UgoCheck' || field.control === 'UgoMultiSelect' ||
      field.control === 'ZzlCheck' || field.control === 'ZzlMultiSelect') {
    return buildValueDef({
      type: 'array',
      key: field.key,
      name: field.name,
      option: buildSelectFieldOption(cfg, field.control),
    });
  }
  const type = SCHEMA_CONTROL_TYPE[field.control] || 'string';
  return buildValueDef({
    type,
    key: field.key,
    name: field.name,
    option: {...cfg},
  });
}

export function nestedFormValueDef(field, getChildDef) {
  const fields = typeof getChildDef === 'function' ?
    getChildDef(field.fields || []) :
    [];
  return objectValueDef(field, fields, {
    model: field.config && field.config.model,
    description: '内嵌表单对象',
  });
}

/** 多重表行内通用键（id / order / 克隆键）说明，避免与业务 fields 重复 */
export function enrichMultiFormItemFields(itemFields = [], config = {}) {
  const keys = new Set((itemFields || []).map((f) => f && f.key).filter(Boolean));
  const meta = [];
  const cloneKey = config.cloneKey && String(config.cloneKey).trim();

  if (cloneKey && !keys.has(cloneKey)) {
    meta.push(buildValueDef({
      type: 'string',
      key: cloneKey,
      name: '克隆键值',
      option: {
        required: true,
        description: `克隆模式必填；值来自 model.${config.cloneBy || 'cloneBy'}`,
      },
    }));
  } else if (!cloneKey && !keys.has('id')) {
    meta.push(buildValueDef({
      type: 'number',
      key: 'id',
      name: '记录ID',
      option: {
        optional: true,
        description: '已有记录填写用于更新；新建行省略 id',
      },
    }));
  }
  if (!keys.has('order')) {
    meta.push(buildValueDef({
      type: 'number',
      key: 'order',
      name: '排序',
      option: {
        optional: true,
        description: '组件按行号自动写入 order=(行号+1)，一般无需手动填写',
      },
    }));
  }
  return [...meta, ...(itemFields || [])];
}

/**
 * 多重表(MultiForm) 值类型说明：顶层 array，元素 object
 */
export function buildMultiFormFieldOption(config = {}, {fieldKey, itemFields = []} = {}) {
  const keys = (itemFields || []).map((f) => f.key).filter(Boolean);
  const cloneKey = config.cloneKey && String(config.cloneKey).trim();
  const keyHint = fieldKey ? `提交键 "${fieldKey}"` : '本字段';

  let description = `多重表(MultiForm)：${keyHint} 的类型为 array<object>（对象数组）`;
  description += '；JSON 中必须是数组，不能是单个对象';
  description += '；数组每项为一条记录，子字段键见 fields（禁止用中文 name 作键）';
  if (cloneKey) {
    description += `；克隆模式：每行必含 "${cloneKey}"`;
    if (config.cloneBy) {
      description += `，值与 model 路径 ${config.cloneBy} 对应`;
    }
  } else {
    description += '；新建行可省略 id；order 由组件自动生成';
  }

  return {
    control: 'UgoMultiForm',
    valueAs: 'array',
    itemType: 'object',
    itemValueAs: 'object',
    model: config.model,
    cloneKey: cloneKey || null,
    cloneBy: config.cloneBy || null,
    description,
    valueShape: keys.length ?
      `[{ ${keys.join(', ')} }]` :
      '[{ /* 每项键见 fields */ }]',
    itemCount: '可变长度（0 项表示空表）',
  };
}

export function multiFormValueDef(field, getChildDef) {
  const cfg = field.config || {};
  const itemFieldsRaw = typeof getChildDef === 'function' ?
    getChildDef(field.fields || []) :
    [];
  const itemFields = enrichMultiFormItemFields(itemFieldsRaw, cfg);
  return buildValueDef({
    type: 'array',
    key: field.key,
    name: field.name,
    option: buildMultiFormFieldOption(cfg, {fieldKey: field.key, itemFields}),
    fields: itemFields,
  });
}

/** 仅收集 valueDef 顶层字段 key（不把 array 行内 fields 当作表单顶层键） */
export function collectTopLevelValueDefKeys(defs) {
  const keys = new Set();
  (defs || []).forEach((d) => {
    if (d && d.key) {
      keys.add(d.key);
    }
  });
  return keys;
}

const COL_CONTROL_VALUE_TYPE = {
  Input: 'string',
  UgoInput: 'string',
  Textarea: 'string',
  UgoTextarea: 'string',
  Number: 'number',
  UgoNumber: 'number',
  InputNumber: 'number',
  Date: 'string',
  UgoDate: 'string',
  Datetime: 'string',
  UgoDatetime: 'string',
  Month: 'string',
  UgoMonth: 'string',
  Select: 'string',
  UgoSelect: 'string',
  Radio: 'string',
  UgoRadio: 'string',
  Check: 'array',
  UgoCheck: 'array',
  MultiSelect: 'array',
  UgoMultiSelect: 'array',
  BaseSelect: 'string',
  UserSelect: 'number',
  UgoUserSelect: 'number',
  BaseUserSelectGrouped: 'number',
  'i-switch': 'boolean',
  Mark: 'number',
  UgoMark: 'number',
};

const TABLE_META_CONTROLS = new Set(['drag', 'index']);

export function colControlValueType(control) {
  if (!control) {
    return 'string';
  }
  return COL_CONTROL_VALUE_TYPE[control] || 'string';
}

export function buildTableColFields(cols = [], resolveColDef) {
  return cols
      .filter((col) => col && col.key && !TABLE_META_CONTROLS.has(col.control))
      .map((col) => {
        if (typeof resolveColDef === 'function') {
          const custom = resolveColDef(col);
          if (custom) {
            return custom;
          }
        }
        const type = colControlValueType(col.control);
        const def = buildValueDef({
          type,
          key: col.key,
          name: col.name || col.key,
          option: {
            control: col.control,
            ...(col.config || {}),
            ...(col.props || {}),
          },
        });
        const selectCfg = Object.assign({}, col.config || {}, col.props || {});
        if (isUserSelectControl(col.control)) {
          Object.assign(def.option, buildUserSelectFieldOption(
            {...selectCfg, control: col.control},
            selectCfg.users,
          ));
          def.type = selectCfg.multiple ? 'array' : colControlValueType(col.control);
        } else if (
          isOptionSelectControl(col.control) ||
          selectCfg.options ||
          selectCfg.url ||
          selectCfg.remote
        ) {
          Object.assign(def.option, buildSelectFieldOption(selectCfg, col.control));
        }
        return def;
      });
}

export function tableValueDef(field) {
  const cols = (field.config && field.config.cols) || [];
  return buildValueDef({
    type: 'array',
    key: field.key,
    name: field.name,
    option: {
      itemType: 'object',
      description: '表格行数组，每行键为列 key',
    },
    fields: buildTableColFields(cols),
  });
}

export const CONTRACT_SPLIT_BUSINESS_TYPES = [
  '全过程咨询', '项目管理', '房建监理', '市政监理', '造价咨询', 'BIM咨询', '招标代理', '对外合作', '其他',
];
export const CONTRACT_SPLIT_TYPES = ['自营', '联合体', '外委'];

/** 合同服务内容拆分表列定义（与控件 presetColumns 数据列一致，供 valueDef 生成选项说明） */
export function contractSplitTableColDefs() {
  return [
    {
      key: 'business_type',
      name: '服务类型',
      control: 'BaseSelect',
      props: {
        options: CONTRACT_SPLIT_BUSINESS_TYPES,
        valueType: 'name',
      },
    },
    {key: 'amount', name: '金额', control: 'InputNumber'},
    {
      key: 'type',
      name: '类型',
      control: 'BaseSelect',
      props: {
        options: CONTRACT_SPLIT_TYPES,
        valueType: 'name',
      },
    },
    {key: 'deduct_amount', name: '扣除金额', control: 'InputNumber'},
    {key: 'unbilling', name: '对方开票', control: 'i-switch'},
  ];
}

export function contractSplitTableValueDef(field) {
  return buildValueDef({
    type: 'array',
    key: field.key,
    name: field.name,
    option: {
      itemType: 'object',
      amountKey: field.config && field.config.amountKey,
    },
    fields: buildTableColFields(contractSplitTableColDefs()),
  });
}

export function userSelectValueDef(field) {
  const cfg = field.config || {};
  const multiple = !!cfg.multiple;
  return buildValueDef({
    type: multiple ? 'array' : 'number',
    key: field.key,
    name: field.name,
    option: buildUserSelectFieldOption({...cfg, control: field.control || 'UgoUserSelect'}),
  });
}

export function datetimeRangeValueDef(field) {
  return buildValueDef({
    type: 'array',
    key: field.key,
    name: field.name,
    option: {
      length: 2,
      itemType: 'string',
      description: '[开始时间, 结束时间]，格式 YYYY-MM-DD HH:mm:ss',
    },
  });
}

export function zzlPreselectValueDef(field) {
  const cfg = field.config || {};
  const simple = cfg.simple !== false;
  const baseOptions = [
    {key: 0, name: '选用'},
    {key: 1, name: '不选用'},
    {key: 2, name: '自定义'},
  ];
  const options = simple ?
    baseOptions.map((o) => ({key: o.name, name: o.name})) :
    baseOptions;
  return buildValueDef({
    type: 'string',
    key: field.key,
    name: field.name,
    option: {
      ...selectOptionValueMeta({simple, ...cfg}, 'UgoZzlPreselect'),
      options,
    },
  });
}
